import React, { useState, useEffect } from "react";
import { Link, useLocation, Routes, Route } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMenuAccess } from "../contexts/MenuAccessContext";
import LeadsManagement from "./LeadsManagement";
import DailyTasksSection from "./DailyTasksSection";
import AppointmentsSection from "./AppointmentsSection";
import OrdersSection from "./OrdersSection";

// Universal Dashboard Component that can be used by Sales, Orders, and Telecaller dashboards
const UniversalDashboard = ({
  dashboardType,
  title,
  description,
  defaultComponent: DefaultComponent,
  additionalMenuItems = [],
  statsCards = null,
}) => {
  const { user } = useAuth();
  const { getCurrentUserAccess, loading, menuAccess } = useMenuAccess();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [menuItems, setMenuItems] = useState([]);

  const isActive = (path) => {
    return location.pathname.includes(path);
  };

  // Update menu items when menu access loads
  useEffect(() => {
    if (!loading && user) {
      const leadsAccess = getCurrentUserAccess("leads");
      const appointmentsAccess = getCurrentUserAccess("appointments");
      const ordersAccess = getCurrentUserAccess("orders");
      const tasksAccess = getCurrentUserAccess("tasks");

      const items = [
        {
          id: "overview",
          label: "📊 Overview",
          path: `/${dashboardType}`,
          component: DefaultComponent,
          hasAccess: true, // Always available
        },
        {
          id: "leads",
          label: "👥 Leads",
          path: `/${dashboardType}/leads`,
          component: () => <LeadsManagement userRole={user?.role} />,
          hasAccess: leadsAccess,
        },
        {
          id: "appointments",
          label: "📅 Appointments",
          path: `/${dashboardType}/appointments`,
          component: AppointmentsSection,
          hasAccess: appointmentsAccess,
        },
        {
          id: "orders",
          label: "📦 Orders",
          path: `/${dashboardType}/orders`,
          component: OrdersSection,
          hasAccess: ordersAccess,
        },
        {
          id: "tasks",
          label: "✅ Tasks",
          path: `/${dashboardType}/tasks`,
          component: DailyTasksSection,
          hasAccess: tasksAccess,
        },
        ...additionalMenuItems,
      ];

      setMenuItems(items);
    }
  }, [loading, menuAccess, user?.role, dashboardType, getCurrentUserAccess]);

  // Show loading state while menu access is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const accessibleMenuItems = menuItems.filter((item) => item.hasAccess);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">{user?.name}</p>
          </div>
          <nav className="mt-6">
            <div className="px-6 space-y-1">
              {accessibleMenuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`block w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === item.id
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
            <p className="text-gray-600 mt-2">{description}</p>
          </div>

          {/* Stats Cards (if provided) */}
          {statsCards && <div className="mb-8">{statsCards}</div>}

          {/* Dynamic Content */}
          <div>
            {(() => {
              const activeMenuItem = accessibleMenuItems.find(
                (item) => item.id === activeTab
              );
              if (activeMenuItem && activeMenuItem.component) {
                const Component = activeMenuItem.component;
                return <Component />;
              }
              return <DefaultComponent />;
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversalDashboard;
